import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Hexagon, Fingerprint, ShieldCheck, KeySquare, Lock, LockKeyhole, ShieldAlert, Timer, Unlock } from "lucide-react";
import { YubiKey3D } from "../YubiKey3D";
import { LangSwitch } from "../LangSwitch";
import { ThemeSwitch } from "../ThemeSwitch";
import { Btn, Overline } from "../ui";
import { SCREENS } from "../../lib/constants";
import { http } from "../../lib/api";
import { useI18n } from "../../lib/i18n";

const LOCKED = SCREENS.filter((s) => s.id !== "auth");

function StageRail({ stage }) {
  const { t } = useI18n();
  const steps = [
    { id: "gate", num: "01", label: t("gate.step1") },
    { id: "pin", num: "02", label: t("gate.step2") },
  ];
  return (
    <div className="flex items-center gap-2" data-testid="gate-stage-rail">
      {steps.map((s, i) => {
        const done = stage === "pin" && s.id === "gate";
        const active = stage === s.id;
        return (
          <React.Fragment key={s.id}>
            <div
              className={`flex items-center gap-2 rounded-full border px-3 py-1 ${
                done
                  ? "border-aegis-emerald/40 bg-aegis-emerald/10 text-aegis-emerald"
                  : active
                  ? "border-aegis-cyan/40 bg-aegis-cyan/10 text-aegis-cyan"
                  : "border-edge text-slate-400"
              }`}
              data-testid={`gate-stage-${s.id}`}
            >
              <span className="font-mono text-[10px]">{s.num}</span>
              <span className="text-[11px] font-medium">{s.label}</span>
              {done && <ShieldCheck size={12} />}
            </div>
            {i === 0 && <span className="h-px w-4 bg-edgeStrong" />}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function LockedInventory({ stage, onPoke }) {
  const { t } = useI18n();
  return (
    <div data-testid="locked-inventory">
      <Overline>{t("gate.inventory")}</Overline>
      <div className="mt-3 grid grid-cols-2 gap-1.5 xl:grid-cols-3">
        {LOCKED.map((s) => (
          <button
            key={s.id}
            data-testid={`locked-screen-${s.id}`}
            onClick={() => onPoke(s)}
            className="flex items-center gap-2 rounded-lg border border-edge raise px-2.5 py-2 text-left transition-colors duration-200 hover:border-aegis-amber/40"
          >
            <span className="font-mono text-[10px] text-slate-400">{s.num}</span>
            <span className="min-w-0 flex-1 truncate text-[11px] text-slate-400">{t(s.key)}</span>
            {stage === "pin" ? (
              <Unlock size={12} className="shrink-0 text-aegis-amber" />
            ) : (
              <Lock size={12} className="shrink-0 text-slate-500" />
            )}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function AuthPortal({ gate, onGate, onAuth, onRelock }) {
  const { t } = useI18n();
  const stage = gate ? "pin" : "gate";
  const [passcode, setPasscode] = useState("");
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [keyState, setKeyState] = useState("idle");
  const [error, setError] = useState("");
  const [attempts, setAttempts] = useState(null);
  const [lockFor, setLockFor] = useState(0);
  const [hint, setHint] = useState("");
  const [policy, setPolicy] = useState({ max_attempts: 5, lockout_minutes: 15 });

  useEffect(() => {
    http
      .get("/gate/state")
      .then(({ data }) => {
        setPolicy(data);
        if (data.locked) setLockFor(data.retry_after);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (lockFor <= 0) return undefined;
    const id = setInterval(() => setLockFor((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, [lockFor]);

  useEffect(() => {
    if (!hint) return undefined;
    const id = setTimeout(() => setHint(""), 4000);
    return () => clearTimeout(id);
  }, [hint]);

  const pokeLocked = (s) => setHint(stage === "pin" ? t("gate.need_key", { n: t(s.key) }) : t("gate.need_gate", { n: t(s.key) }));

  const submitGate = async () => {
    if (!passcode || busy || lockFor > 0) return;
    setBusy(true);
    setError("");
    const res = await onGate(passcode.trim());
    setBusy(false);
    if (res?.ok) {
      setPasscode("");
      setAttempts(null);
      return;
    }
    setPasscode("");
    if (res?.reason === "locked") {
      setLockFor(res.lockedFor || 900);
      setError(t("gate.locked_out"));
      return;
    }
    setAttempts(res?.attemptsRemaining ?? null);
    setError(t("gate.invalid"));
  };

  const submitPin = async () => {
    if (!pin || busy) return;
    setBusy(true);
    setError("");
    setKeyState("reading");
    const ok = await onAuth(pin);
    if (!ok) {
      setKeyState("denied");
      setError(t("auth.error"));
      setBusy(false);
      setPin("");
      return;
    }
    setKeyState("verified");
  };

  return (
    <div className="app-shell min-h-screen w-full" data-testid="screen-auth">
      <div className="mx-auto grid min-h-screen max-w-[1500px] grid-cols-1 lg:grid-cols-[1.05fr_1fr]">
        <div className="relative flex flex-col justify-between gap-10 border-b border-edge px-8 py-10 lg:border-b-0 lg:border-r lg:px-14 lg:py-14">
          <div className="flex items-center gap-3">
            <span className="grid place-items-center h-11 w-11 rounded-xl border border-aegis-cyan/30 bg-aegis-cyan/10 text-aegis-cyan">
              <Hexagon size={22} strokeWidth={1.6} />
            </span>
            <div>
              <p className="font-heading text-[18px] font-semibold tracking-tight text-slate-100">{t("brand.name")}</p>
              <p className="text-[11px] text-slate-400">{t("brand.tagline")}</p>
            </div>
          </div>

          <div>
            <Overline>{t("auth.overline")}</Overline>
            <h1 className="mt-3 font-heading text-4xl font-light leading-[1.08] tracking-tighter text-slate-100 sm:text-5xl">
              {stage === "gate" ? t("gate.title") : t("auth.title")}
            </h1>
            <p className="mt-5 max-w-lg text-sm leading-relaxed text-slate-400">
              {stage === "gate" ? t("gate.sub") : t("auth.sub")}
            </p>

            <motion.div
              initial={{ opacity: 0, y: 22 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1, duration: 0.6 }}
              className="mt-8 max-w-[460px]"
            >
              <YubiKey3D state={stage === "gate" ? "idle" : keyState} className="w-full" />
            </motion.div>

            <div className="mt-5 flex items-center gap-3">
              <span
                className={`h-2 w-2 rounded-full animate-pulseGlow ${
                  stage === "gate"
                    ? "bg-slate-600"
                    : keyState === "verified"
                    ? "bg-aegis-emerald"
                    : keyState === "denied"
                    ? "bg-aegis-red"
                    : keyState === "reading"
                    ? "bg-aegis-amber"
                    : "bg-slate-600"
                }`}
              />
              <span className="font-mono text-[11px] text-slate-400" data-testid="key-state">
                {stage === "gate" ? t("gate.state.sealed") : t(`auth.state.${keyState}`)}
              </span>
            </div>
          </div>

          <ul className="hidden space-y-2.5 lg:block">
            {[
              [ShieldCheck, t("auth.f1")],
              [KeySquare, t("auth.f2")],
              [Lock, t("auth.f3")],
            ].map(([Icon, label]) => (
              <li key={label} className="flex items-center gap-2.5 text-[12px] text-slate-400">
                <Icon size={14} className="text-aegis-cyan/70" />
                {label}
              </li>
            ))}
          </ul>
        </div>

        <div className="flex flex-col justify-center gap-8 px-8 py-12 lg:px-14">
          <div className="flex items-center justify-between gap-3">
            <StageRail stage={stage} />
            <div className="flex items-center gap-2">
              <ThemeSwitch />
              <LangSwitch />
            </div>
          </div>

          <motion.div
            key={stage}
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="rounded-2xl border border-edge bg-panel/80 p-7 lg:p-9"
          >
            {stage === "gate" ? (
              <>
                <div className="flex items-center gap-3">
                  <span className="grid place-items-center h-10 w-10 rounded-lg border border-aegis-cyan/35 bg-aegis-cyan/10 text-aegis-cyan">
                    <LockKeyhole size={19} />
                  </span>
                  <div>
                    <p className="font-heading text-[15px] text-slate-100">{t("gate.card")}</p>
                    <p className="font-mono text-[10px] text-slate-400">PERIMETER-GATE · HS256 / BCRYPT</p>
                  </div>
                </div>

                <div className="mt-8">
                  <label className="text-[10px] uppercase tracking-overline text-slate-400">{t("gate.label")}</label>
                  <input
                    data-testid="gate-passcode-input"
                    type="password"
                    value={passcode}
                    autoFocus
                    disabled={lockFor > 0}
                    onChange={(e) => {
                      setPasscode(e.target.value);
                      setError("");
                    }}
                    onKeyDown={(e) => e.key === "Enter" && submitGate()}
                    placeholder="••••••••••••"
                    className="mt-2 w-full rounded-lg border border-edge well px-4 py-3.5 text-center font-mono text-base tracking-[0.35em] text-slate-100 placeholder:text-slate-500 focus:border-slate-600 focus:outline-none focus:ring-1 focus:ring-slate-500 disabled:opacity-50"
                  />
                </div>

                {lockFor > 0 && (
                  <p
                    className="mt-3 flex items-center gap-2 rounded-lg border border-aegis-red/35 bg-aegis-red/10 px-3 py-2 text-[12px] text-aegis-red"
                    data-testid="gate-lockout"
                  >
                    <Timer size={13} /> {t("gate.lock_timer", { s: lockFor })}
                  </p>
                )}

                {error && lockFor === 0 && (
                  <p
                    className="mt-3 rounded-lg border border-aegis-red/35 bg-aegis-red/10 px-3 py-2 text-[12px] text-aegis-red"
                    data-testid="gate-error"
                  >
                    {error}
                    {attempts !== null && ` · ${t("gate.attempts", { n: attempts })}`}
                  </p>
                )}

                <Btn
                  variant="solidCyan"
                  onClick={submitGate}
                  disabled={!passcode || busy || lockFor > 0}
                  testid="gate-submit-btn"
                  className="mt-6 w-full py-3"
                >
                  <LockKeyhole size={16} /> {busy ? t("gate.busy") : t("gate.cta")}
                </Btn>

                <p className="mt-5 flex items-start gap-2 text-[11px] leading-relaxed text-slate-400">
                  <ShieldAlert size={13} className="mt-0.5 shrink-0 text-aegis-amber" />
                  {t("gate.note", { max: policy.max_attempts, mins: policy.lockout_minutes })}
                </p>
              </>
            ) : (
              <>
                <div className="flex items-center gap-3">
                  <span className="grid place-items-center h-10 w-10 rounded-lg border border-aegis-amber/35 bg-aegis-amber/10 text-aegis-amber">
                    <Fingerprint size={19} />
                  </span>
                  <div>
                    <p className="font-heading text-[15px] text-slate-100">{t("auth.key")}</p>
                    <p className="font-mono text-[10px] text-slate-400">YK-5C-NFC-0x9482 · packed / ES256</p>
                  </div>
                </div>

                <div
                  className="mt-5 flex items-center justify-between gap-3 rounded-lg border border-aegis-emerald/35 bg-aegis-emerald/10 px-3 py-2"
                  data-testid="gate-passed-chip"
                >
                  <span className="flex items-center gap-2 text-[11px] text-aegis-emerald" data-testid="gate-passed-label">
                    <ShieldCheck size={13} /> {t("gate.passed", { n: gate.worm_block ?? "—" })}
                  </span>
                  <button
                    onClick={onRelock}
                    data-testid="gate-relock-btn"
                    className="font-mono text-[10px] text-slate-400 underline decoration-dotted hover:text-aegis-red"
                  >
                    {t("gate.relock")}
                  </button>
                </div>

                <div className="mt-6">
                  <label className="text-[10px] uppercase tracking-overline text-slate-400">{t("auth.pin")}</label>
                  <input
                    data-testid="auth-pin-input"
                    type="password"
                    inputMode="numeric"
                    value={pin}
                    autoFocus
                    onChange={(e) => {
                      setPin(e.target.value);
                      setError("");
                      if (keyState === "denied") setKeyState("idle");
                    }}
                    onKeyDown={(e) => e.key === "Enter" && submitPin()}
                    placeholder="• • • • • •"
                    className="mt-2 w-full rounded-lg border border-edge well px-4 py-3.5 text-center font-mono text-lg tracking-[0.5em] text-slate-100 placeholder:text-slate-500 focus:border-slate-600 focus:outline-none focus:ring-1 focus:ring-slate-500"
                  />
                </div>

                {error && (
                  <p
                    className="mt-3 rounded-lg border border-aegis-red/35 bg-aegis-red/10 px-3 py-2 text-[12px] text-aegis-red"
                    data-testid="auth-error"
                  >
                    {error}
                  </p>
                )}

                <Btn
                  variant="solidCyan"
                  onClick={submitPin}
                  disabled={!pin || busy}
                  testid="auth-submit-btn"
                  className="mt-6 w-full py-3"
                >
                  <Fingerprint size={16} /> {busy ? t("auth.busy") : t("auth.cta")}
                </Btn>

                <p className="mt-5 text-center text-[11px] text-slate-400">{t("auth.note")}</p>
              </>
            )}
          </motion.div>

          <div>
            <LockedInventory stage={stage} onPoke={pokeLocked} />
            {hint && (
              <p
                className="mt-3 rounded-lg border border-aegis-amber/35 bg-aegis-amber/10 px-3 py-2 text-[11px] text-aegis-amber"
                data-testid="gate-locked-hint"
              >
                {hint}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
